import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControlLabel,
  Switch,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Bluetooth,
  Nfc,
  LocationOn,
  Battery20,
  Battery50,
  Battery80,
  BatteryFull,
} from '@mui/icons-material';
import { api, endpoints } from '../config/api';
import { Device } from '../types';

const Devices: React.FC = () => {
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [formData, setFormData] = useState<Partial<Device>>({
    macAddress: '',
    name: '',
    type: 'beacon',
    rssi: undefined,
    batteryLevel: undefined,
    isActive: true,
    longitude: undefined,
    latitude: undefined,
    accuracy: undefined,
    altitude: undefined,
    majorId: undefined,
    minorId: undefined,
  });

  useEffect(() => {
    fetchDevices();
  }, []);

  const fetchDevices = async () => {
    try {
      setLoading(true);
      const response = await api.get(endpoints.devices);
      setDevices(response.data.data || []);
    } catch (err) {
      setError('Failed to fetch devices');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedDevice(null);
    setFormData({
      macAddress: '',
      name: '',
      type: 'beacon',
      rssi: undefined,
      batteryLevel: undefined,
      isActive: true,
      longitude: undefined,
      latitude: undefined,
      accuracy: undefined,
      altitude: undefined,
      majorId: undefined,
      minorId: undefined,
    });
    setDialogOpen(true);
  };

  const handleEdit = (device: Device) => {
    setSelectedDevice(device);
    setFormData({
      macAddress: device.macAddress,
      name: device.name,
      type: device.type,
      rssi: device.rssi,
      batteryLevel: device.batteryLevel,
      isActive: device.isActive,
      longitude: device.longitude,
      latitude: device.latitude,
      accuracy: device.accuracy,
      altitude: device.altitude,
      majorId: device.majorId,
      minorId: device.minorId,
    });
    setDialogOpen(true);
  };

  const handleDelete = (device: Device) => {
    setSelectedDevice(device);
    setDeleteDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (selectedDevice) {
        await api.put(endpoints.device(selectedDevice.macAddress), formData);
        setSuccess('Device updated successfully');
      } else {
        await api.post(endpoints.devices, formData);
        setSuccess('Device created successfully');
      }
      setDialogOpen(false);
      fetchDevices();
    } catch (err) {
      setError('Failed to save device');
      console.error(err);
    }
  };

  const confirmDelete = async () => {
    if (selectedDevice) {
      try {
        await api.delete(endpoints.device(selectedDevice.macAddress));
        setSuccess('Device deleted successfully');
        setDeleteDialogOpen(false);
        fetchDevices();
      } catch (err) {
        setError('Failed to delete device');
        console.error(err);
      }
    }
  };

  const getDeviceIcon = (type: string) => {
    switch (type) {
      case 'beacon':
        return <Bluetooth />;
      case 'nfc':
        return <Nfc />;
      case 'gps':
        return <LocationOn />;
      default:
        return <Bluetooth />;
    }
  };

  const getBatteryIcon = (level?: number) => {
    if (!level) return null;
    if (level > 75) return <BatteryFull color="success" />;
    if (level > 50) return <Battery80 color="success" />;
    if (level > 20) return <Battery50 color="warning" />;
    return <Battery20 color="error" />;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading devices...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Devices</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
          Add Device
        </Button>
      </Box>

      {devices.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="body1" align="center">
              No devices found. Add your first device to get started.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>MAC Address</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Battery</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {devices.map((device) => (
                <TableRow key={device.id}>
                  <TableCell>{getDeviceIcon(device.type)}</TableCell>
                  <TableCell>{device.name}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {device.macAddress}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={device.isActive ? 'Active' : 'Inactive'}
                      color={device.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {device.batteryLevel ? (
                      <Box display="flex" alignItems="center" gap={1}>
                        {getBatteryIcon(device.batteryLevel)}
                        <Typography variant="body2">{device.batteryLevel}%</Typography>
                      </Box>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {device.longitude && device.latitude ? (
                      <Typography variant="body2">
                        {device.latitude.toFixed(6)}, {device.longitude.toFixed(6)}
                      </Typography>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleEdit(device)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(device)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Device Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{selectedDevice ? 'Edit Device' : 'Add Device'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Device Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="MAC Address"
                value={formData.macAddress}
                onChange={(e) => setFormData({ ...formData, macAddress: e.target.value })}
                placeholder="XX:XX:XX:XX:XX:XX"
                disabled={!!selectedDevice}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Device Type</InputLabel>
                <Select
                  value={formData.type}
                  label="Device Type"
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as 'beacon' | 'nfc' | 'gps' })}
                >
                  <MenuItem value="beacon">Beacon</MenuItem>
                  <MenuItem value="nfc">NFC</MenuItem>
                  <MenuItem value="gps">GPS</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>
            {formData.type === 'beacon' && (
              <>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    label="RSSI"
                    type="number"
                    value={formData.rssi || ''}
                    onChange={(e) => setFormData({ ...formData, rssi: e.target.value ? parseInt(e.target.value) : undefined })}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    label="Major ID"
                    type="number"
                    value={formData.majorId || ''}
                    onChange={(e) => setFormData({ ...formData, majorId: e.target.value ? parseInt(e.target.value) : undefined })}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    label="Minor ID"
                    type="number"
                    value={formData.minorId || ''}
                    onChange={(e) => setFormData({ ...formData, minorId: e.target.value ? parseInt(e.target.value) : undefined })}
                  />
                </Grid>
              </>
            )}
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Battery Level (%)"
                type="number"
                value={formData.batteryLevel || ''}
                onChange={(e) => setFormData({ ...formData, batteryLevel: e.target.value ? parseInt(e.target.value) : undefined })}
                InputProps={{ inputProps: { min: 0, max: 100 } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Latitude"
                type="number"
                value={formData.latitude || ''}
                onChange={(e) => setFormData({ ...formData, latitude: e.target.value ? parseFloat(e.target.value) : undefined })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Longitude"
                type="number"
                value={formData.longitude || ''}
                onChange={(e) => setFormData({ ...formData, longitude: e.target.value ? parseFloat(e.target.value) : undefined })}
              />
            </Grid>
            {formData.type === 'gps' && (
              <>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Accuracy (m)"
                    type="number"
                    value={formData.accuracy || ''}
                    onChange={(e) => setFormData({ ...formData, accuracy: e.target.value ? parseFloat(e.target.value) : undefined })}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Altitude (m)"
                    type="number"
                    value={formData.altitude || ''}
                    onChange={(e) => setFormData({ ...formData, altitude: e.target.value ? parseFloat(e.target.value) : undefined })}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!formData.name || !formData.macAddress}
          >
            {selectedDevice ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the device "{selectedDevice?.name}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notifications */}
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError('')}>
        <Alert severity="error" onClose={() => setError('')}>
          {error}
        </Alert>
      </Snackbar>
      <Snackbar open={!!success} autoHideDuration={4000} onClose={() => setSuccess('')}>
        <Alert severity="success" onClose={() => setSuccess('')}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Devices;